import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import type { SortOption } from '@iconik-io/shared-lib/src/utils/sort/types';

import type { PaginationOptions } from '../../types/common';
import type {
  CommonListAppState,
  CommonListGetListError,
  CommonListPrefix,
} from './types';

export const defaultPaginationState = {
  page: 1,
  pages: 1,
  total: 0,
  per_page: 0,
};

export const defaultInitialState: CommonListAppState = {
  ids: [],
  pagination: defaultPaginationState,
  sort: [
    {
      name: 'name',
      order: 'asc',
    },
  ],
  loading: false,
};

const createCommonListSlice = (
  prefix: CommonListPrefix,
  initialState: CommonListAppState,
) => {
  // Set of ids to quickly check if id is already in the list.
  // Makes sense for jobs dashboard lists where amount of updates can become enormous and
  // it's vital to be performant in checking uniqueness of ids there
  // to reduce unnecessary state updates and component renders.
  let listIds = new Set<string>();

  const ensureIdExists = (id: string): boolean => {
    if (listIds.has(id)) {
      return true;
    }
    listIds.add(id);
    return false;
  };

  return createSlice({
    name: prefix,
    initialState,
    reducers: {
      setIds: (state, action: PayloadAction<string[]>) => {
        listIds = new Set(action.payload);
        state.ids = action.payload;
      },

      prependId: (state, action: PayloadAction<string>) => {
        if (ensureIdExists(action.payload)) {
          return;
        }
        state.ids.unshift(action.payload);
      },

      appendId: (state, action: PayloadAction<string>) => {
        if (ensureIdExists(action.payload)) {
          return;
        }
        state.ids.push(action.payload);
      },

      appendIds: (state, action: PayloadAction<string[]>) => {
        const idsToAppend: string[] = [];
        action.payload.forEach((id) => {
          if (!ensureIdExists(id)) {
            idsToAppend.push(id);
          }
        });
        if (idsToAppend.length === 0) {
          return;
        }
        state.ids = state.ids.concat(idsToAppend);
      },

      deleteId: (state, action: PayloadAction<string>) => {
        listIds.delete(action.payload);
        state.ids = state.ids.filter((id) => id !== action.payload);
        state.pagination.total = Math.max(state.pagination.total - 1, 0);
      },

      deleteIds: (state, action: PayloadAction<string[]>) => {
        const idsToDelete = new Set(action.payload);
        idsToDelete.forEach((id) => {
          listIds.delete(id);
        });
        state.ids = state.ids.filter((id) => !idsToDelete.has(id));
        state.pagination.total = Math.max(
          state.pagination.total - idsToDelete.size,
          0,
        );
      },

      setPagination: (state, action: PayloadAction<PaginationOptions>) => {
        state.pagination = action.payload;
      },

      resetPagination: (state) => {
        state.pagination = { ...initialState.pagination };
      },

      setSort: (state, action: PayloadAction<SortOption[]>) => {
        state.sort = action.payload;
      },

      setLoading: (state, action: PayloadAction<boolean>) => {
        state.loading = action.payload;
        if (action.payload) {
          state.latestError = undefined;
        }
      },

      resetListData: (state) => {
        listIds.clear();
        const { ids, pagination, loading } = defaultInitialState;
        state.ids = [...ids];
        state.pagination = { ...pagination };
        state.loading = loading;
      },

      setLatestError: (
        state,
        action: PayloadAction<CommonListGetListError>,
      ) => {
        state.latestError = action.payload;
      },
    },
  });
};

/**
 * Action creators only depend on the prefix, so they are shared by every
 * reducer instance created for it, while each reducer keeps its own initial
 * state and its own set of ids.
 */
export const commonListActions = memoize(
  (prefix: CommonListPrefix) =>
    createCommonListSlice(prefix, defaultInitialState).actions,
);

export const commonListReducer = (
  prefix: CommonListPrefix,
  initialState: CommonListAppState = defaultInitialState,
) => createCommonListSlice(prefix, initialState).reducer;

export type CommonListActionTypes = ReturnType<typeof commonListActions>;
